import { ACCESS_LEVELS, MEMORY_CLASSES, SEARCH_INPUT_LIMITS, SOURCE_TYPES } from '@agentwiki/shared'

export const LIBRARY_QUERY_KEYS = [
  'q',
  'view',
  'ownership',
  'access',
  'source',
  'sync',
  'authority',
  'class',
  'sort',
  'order',
] as const

export type LibraryFixedView = 'all' | 'needs-review' | 'decisions' | 'runbooks' | 'handoffs'
export type LibraryOwnership = 'all' | 'manual' | 'source-backed'
export type LibraryAccess = 'all' | (typeof ACCESS_LEVELS)[number]
export type LibrarySyncHealth = 'all' | 'verified' | 'stale' | 'conflict'
export type LibraryAuthority =
  | 'all'
  | 'active'
  | 'reference'
  | 'needs-review'
  | 'superseded'
  | 'expired'
export type LibraryMemoryClass = 'all' | (typeof MEMORY_CLASSES)[number]
export type LibrarySort = 'updated' | 'created' | 'title'
export type LibrarySortOrder = 'asc' | 'desc'

export interface LibraryQueryState {
  query: string
  view: LibraryFixedView
  ownership: LibraryOwnership
  access: LibraryAccess
  source: string
  sync: LibrarySyncHealth
  authority: LibraryAuthority
  memoryClass: LibraryMemoryClass
  sort: LibrarySort
  order: LibrarySortOrder
}

export const DEFAULT_LIBRARY_QUERY_STATE: Readonly<LibraryQueryState> = Object.freeze({
  query: '',
  view: 'all',
  ownership: 'all',
  access: 'all',
  source: 'all',
  sync: 'all',
  authority: 'all',
  memoryClass: 'all',
  sort: 'updated',
  order: 'desc',
})

const FIXED_VIEWS = ['all', 'needs-review', 'decisions', 'runbooks', 'handoffs'] as const
const OWNERSHIP = ['all', 'manual', 'source-backed'] as const
const ACCESS = ['all', ...ACCESS_LEVELS] as const
const SYNC = ['all', 'verified', 'stale', 'conflict'] as const
const AUTHORITY = ['all', 'active', 'reference', 'needs-review', 'superseded', 'expired'] as const
const MEMORY_CLASS = ['all', ...MEMORY_CLASSES] as const
const SORT = ['updated', 'created', 'title'] as const
const ORDER = ['asc', 'desc'] as const

function firstValue(params: URLSearchParams, key: string) {
  return params.getAll(key)[0] ?? null
}

function enumValue<const T extends readonly string[]>(
  value: string | null,
  allowed: T,
  fallback: T[number],
): T[number] {
  return value && (allowed as readonly string[]).includes(value) ? value : fallback
}

function sourceValue(value: string | null) {
  if (!value || value === 'all') return 'all'
  if (value.length > 512 || /[\u0000-\u001f\u007f]/.test(value)) return 'all'
  const separator = value.indexOf(':')
  if (separator < 1 || separator === value.length - 1) return 'all'
  return (SOURCE_TYPES as readonly string[]).includes(value.slice(0, separator)) ? value : 'all'
}

function serializableSourceValue(value: string) {
  if (value === 'all') return value
  const [sourceType, fingerprint, ...rest] = value.split(':')
  return rest.length === 0 &&
    (SOURCE_TYPES as readonly string[]).includes(sourceType) &&
    /^[a-f0-9]{16}$/.test(fingerprint)
    ? value
    : 'all'
}

function queryValue(value: string | null) {
  const query = value?.trim() ?? ''
  return query.length <= SEARCH_INPUT_LIMITS.query && !/[\u0000-\u001f\u007f]/.test(query)
    ? query
    : ''
}

export function parseLibraryQueryState(params: URLSearchParams): LibraryQueryState {
  return {
    query: queryValue(firstValue(params, 'q')),
    view: enumValue(firstValue(params, 'view'), FIXED_VIEWS, 'all'),
    ownership: enumValue(firstValue(params, 'ownership'), OWNERSHIP, 'all'),
    access: enumValue(firstValue(params, 'access'), ACCESS, 'all'),
    source: sourceValue(firstValue(params, 'source')),
    sync: enumValue(firstValue(params, 'sync'), SYNC, 'all'),
    authority: enumValue(firstValue(params, 'authority'), AUTHORITY, 'all'),
    memoryClass: enumValue(firstValue(params, 'class'), MEMORY_CLASS, 'all'),
    sort: enumValue(firstValue(params, 'sort'), SORT, 'updated'),
    order: enumValue(firstValue(params, 'order'), ORDER, 'desc'),
  }
}

export function serializeLibraryQueryState(state: LibraryQueryState) {
  const params = new URLSearchParams()
  const values: Record<(typeof LIBRARY_QUERY_KEYS)[number], string> = {
    q: state.query.trim(),
    view: state.view,
    ownership: state.ownership,
    access: state.access,
    source: serializableSourceValue(state.source),
    sync: state.sync,
    authority: state.authority,
    class: state.memoryClass,
    sort: state.sort,
    order: state.order,
  }
  const defaults = serializeDefaults()
  for (const key of LIBRARY_QUERY_KEYS) {
    if (values[key] && values[key] !== defaults[key]) params.set(key, values[key])
  }
  return params
}

export function updateLibraryQueryState(
  params: URLSearchParams,
  updates: Partial<LibraryQueryState>,
) {
  return serializeLibraryQueryState({ ...parseLibraryQueryState(params), ...updates })
}

export interface KnowledgeLibraryReturnState {
  returnTo: {
    pathname: '/doc'
    search: string
  }
}

export function buildKnowledgeLibraryReturnState(
  params: URLSearchParams,
): KnowledgeLibraryReturnState {
  const serialized = serializeLibraryQueryState(parseLibraryQueryState(params)).toString()
  return {
    returnTo: {
      pathname: '/doc',
      search: serialized ? `?${serialized}` : '',
    },
  }
}

function serializeDefaults(): Record<(typeof LIBRARY_QUERY_KEYS)[number], string> {
  return {
    q: '',
    view: DEFAULT_LIBRARY_QUERY_STATE.view,
    ownership: DEFAULT_LIBRARY_QUERY_STATE.ownership,
    access: DEFAULT_LIBRARY_QUERY_STATE.access,
    source: DEFAULT_LIBRARY_QUERY_STATE.source,
    sync: DEFAULT_LIBRARY_QUERY_STATE.sync,
    authority: DEFAULT_LIBRARY_QUERY_STATE.authority,
    class: DEFAULT_LIBRARY_QUERY_STATE.memoryClass,
    sort: DEFAULT_LIBRARY_QUERY_STATE.sort,
    order: DEFAULT_LIBRARY_QUERY_STATE.order,
  }
}

export const LIBRARY_FIXED_VIEW_FILTERS = {
  all: {},
  'needs-review': { authority: 'needs-review' },
  decisions: { memoryClass: 'decision-record' },
  runbooks: { memoryClass: 'runbook' },
  handoffs: { memoryClass: 'session-summary', sourceType: 'handoff' },
} as const

export function resolveLibraryFixedView(state: LibraryQueryState) {
  return {
    ...state,
    sourceType: 'all',
    ...LIBRARY_FIXED_VIEW_FILTERS[state.view],
  }
}
